import loadProductVideos from '../../core/storefrontAPI/load-product-videos';

export default class VideoTiles {
  tileStates;
  observer;
  mutationObserver;
  
  constructor() {
    const grid = document.querySelector('[data-collection-grid]');
    if (!grid || grid.dataset.productVideos !== 'true') {
      return;
    }

    // Store loaded state per tile
    this.tileStates = new WeakMap();

    // A new swatch has been selected on a tile visible in the viewport
    document.addEventListener('tile-variant:change', (e) => {
      try {
        const tile = e.data.detail.tile[0] ?? null;
        if (!tile) {
          return;
        }
  
        if (tile && tile.dataset.videoInit === 'true' && tile.dataset.videoTileInViewport === 'true') {
          this.appendVideo(tile, this.tileStates.get(tile).mediaData);
        }
      } catch (error) {
        console.log('Error in tile-variant:change event', error);
      }
    });

    // New tiles has been added to the collection grid
    document.addEventListener('astr-plp-tiles-built', (e) => {
      // console.log('[ASTR] Videos - astr-plp-tiles-built event', e.data);

      if (e.data.from_search) {
        return;
      }

      this.initVideoTiles();
      this.observeNewTiles();
    });
  }

  /**
   * Tile observer
   * 
   * @param {*} tileElement Product tile element
   * @description Observe the tile element to load video when it enters the viewport
   * @returns {void}
   */
  tileObserver = (tileElement) => {
    if (!this.observer) {
      this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
        root: null,
        threshold: 0.2,
      });
    }

    this.tileStates.set(tileElement, { mediaData: null });
    this.observer.observe(tileElement);
  };

  /**
   * Initialize video tiles
   * 
   * @description This function will be called after tiles are appended to the DOM. (init, pagination, etc.)
   * @returns {void}
   */
  initVideoTiles = () => {
    // data-video-init => when the media data has been loaded from Shopify Sttorefront API
    // data-video-observer => when the tile is being observed by the IntersectionObserver
    document.querySelectorAll('[data-collection-grid] [data-product-tile][data-video-observer="false"][data-video-init="false"].has-video').forEach(tile => {
      this.tileObserver(tile);
      tile.setAttribute('data-video-observer', 'true');
    });
  };

  /**
   * Observe new tiles
   * 
   * @description Observe the collection grid for new tiles added to the DOM
   * @returns {void}
   */ 
  observeNewTiles = () => {
    const grid = document.querySelector('[data-collection-grid]');

    if (!grid) return;

    this.mutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (
            node.nodeType === 1 &&
            node.matches &&
            node.matches('data-product-tile') &&
            node.classlist.includes('has-video') &&
            node.getAttribute('data-video-observer') !== 'true' && 
            node.getAttribute('data-video-init') !== 'true'
          ) {
            this.tileObserver(node);
            node.setAttribute('data-video-observer', 'true');
          }

          // If tiles are wrapped in a container, check descendants:
          // if (node.nodeType === 1 && node.querySelectorAll) {
          //   node.querySelectorAll('[data-product-tile].has-video').forEach(tile => {
          //     if (tile.getAttribute('data-video-observer') !== 'true') {
          //       this.tileObserver(tile);
          //       tile.setAttribute('data-video-observer', 'true');
          //     }
          //   });
          // }
        });
      });
    });

    this.mutationObserver.observe(grid, { childList: true, subtree: true });
  };

  /**
   * Load or stop the video when the product tile enters or exits the viewport
   * 
   * @description Intersection observer callback
   * @param {*} entries 
   */
  onIntersection = async (entries) => {
    for (const entry of entries) {
      const tile = entry.target;
      const state = this.tileStates.get(tile);

      if (entry.isIntersecting) {
        tile.dataset.videoTileInViewport = 'true';

        // Tile entered viewport
        if (state.mediaData === null) {
          const media_data = await this.fetchProductVideo(tile);
          state.mediaData = media_data;
          if (media_data.length > 0) {
            this.appendVideo(tile, media_data);
          }
        }
      } else {
        // Tile exited viewport
        tile.dataset.videoTileInViewport = 'false';
        this.stopsVideo(tile);
      }
    }
  };

  /**
   * Fetch product video data - on 1st intersection
   * 
   * @description Fetch product video data from the Storefront API
   * @param {*} tile 
   * @returns {Promise} Product video data
   */
  fetchProductVideo = async (tile) => {
    const product_medias = await loadProductVideos(tile.dataset.productHandle);
    tile.setAttribute('data-video-init', 'true');

    return product_medias;
  };

  /**
   * Find the video in the media data based on the main image, the video is defined after the main image
   * 
   * @description Find the video in the media data based on the main image
   * @param {*} tile
   * @param {*} media_data
   * @returns {string|null} Video URL
   */
  findVideo = (tile, media_data) => {
    // Get the main image of the tile for the selected style
    const main_image = tile.querySelector('[data-product-main-image] img');
    if (!main_image) return null;
    const main_image_url = main_image.dataset.originalSrc;
    if (!main_image_url) return null;

    // Get the position of the image in the media data
    const image_data = media_data.find(media => media.node.mediaContentType === 'IMAGE' && media.node.image.url === main_image_url);
    if (!image_data) return null;
    const image_index = media_data.indexOf(image_data);
    if (image_index === -1) return null;

    // Find out if the video is after the image
    const video_index = image_index + 1;
    // if (video_index >= media_data.length) {
    //   // Default to the pdp video if the plp video is not available
    //   video_index = image_index + 1;
    //   return null;
    // };
    if (media_data[video_index].node.mediaContentType !== 'VIDEO' || media_data[video_index].node.sources.length === 0) return null;

    // Get the video url in mp4 format (available video heights are 480, 720, 1080)
    // We prefer the 720p video if available, otherwise we return the first one
    const video_url = media_data[video_index].node.sources.find(source => source.format === 'mp4' && source.height === 720);
    if (video_url) {
      return video_url.url;
    }

    // If no match format, return the first one
    const first_video_url = media_data[video_index].node.sources[0];
    if (first_video_url) {
      return first_video_url.url;
    }

    // If no video found, return null
    return null;
  };

  /**
   * Append the video to the tile on 1st intersection and on swatch change
   * 
   * @description Append video to the tile
   * @param {*} tile 
   * @param {*} media_data 
   */
  appendVideo = (tile, media_data) => {
    // Check if the video is already there, if it's there, it means that it belongs to another style
    const video = tile.querySelector('video');
    if (video) {
      // If a video is already there, we stop and remove it
      this.stopsVideo(tile);
      video.remove();
    }

    // Find the video in the media data based on the main image
    const video_url = this.findVideo(tile, media_data);
    console.log('video_url', video_url);
    if (!video_url) {
      tile.classList.remove('video-visible');
      return;
    };

    // Get the main image alt description
    let main_image_alt = '';
    const main_image = tile.querySelector('[data-product-main-image] img');
    if (main_image) {
      main_image_alt = 'Video of product featuring: ' + main_image.getAttribute('alt');
    }
    if (!main_image_alt) {
      main_image_alt = 'Video of product ' + tile.querySelector('[data-product-tile-title] a').textContent;
    }

    // Prepend the new video
    const video_element = document.createElement('video');
    video_element.setAttribute('playsinline', '');
    video_element.autoplay = true;
    video_element.setAttribute('autoplay', '');
    // video_element.setAttribute('loop', '');
    video_element.muted = true;
    video_element.setAttribute('muted', 'muted');
    video_element.setAttribute('preload', 'auto');
    video_element.setAttribute('disablepictureinpicture', '');
    video_element.setAttribute('data-product-main-video', '');
    video_element.classList.add('product-tile__video');
    video_element.setAttribute('width', '100%');
    video_element.setAttribute('tabindex', '0');
    video_element.setAttribute('aria-label', main_image_alt);
    video_element.setAttribute('aria-description', 'This video only visualizes the product and does not provide additional information');

    const source_element = document.createElement('source');
    source_element.setAttribute('src', video_url);
    source_element.setAttribute('type', 'video/mp4');
    video_element.appendChild(source_element);

    tile.querySelector('[data-product-tile-image]').prepend(video_element);
    tile.classList.add('video-visible');

    // [ADA] Add a status element to the tile
    const status_element = tile.querySelector('[data-video-status]');
    if (status_element) {
      status_element.textContent = 'Product video is playing';
    }
    video_element.addEventListener('ended', () => {
      if (status_element) status_element.textContent = 'Product video stopped';
      this.stopsVideo(tile);
    });
    video_element.addEventListener('pause', () => {
      if (status_element) status_element.textContent = 'Product video paused';
    });
    video_element.addEventListener('play', () => {
      if (status_element) status_element.textContent = 'Product video is playing';
    });

    // Need to wait a little due to append() & buffer
    window.setTimeout(() => {
      video_element.play().catch((error) => {
        // Only log real errors, ignore AbortError from rapid pause/remove
        if (error.name !== 'AbortError') {
          console.log('Error playing video', error);
        }
      });
    }, 100);

    // When the video reaches the end, go back to 1st frame
    video_element.addEventListener('ended', () => {
      this.stopsVideo(tile);
    });

    // On video hover, play the video if not already playing
    // Only add mouseenter once per tile
    if (!tile.hasAttribute('data-video-hover-listener')) {
      tile.setAttribute('data-video-hover-listener', 'true');

      tile.addEventListener('mouseenter', (e) => {
        const tile = e.currentTarget;
        if (!tile.classList.contains('video-visible')) {
          return;
        }

        const video = tile.querySelector('video');
        if (!video) {
          return;
        }
        
        // If the video is already playing, do nothing
        if (!video.paused) {
          return;
        }
        console.log('Video hover', video.paused);

        video.currentTime = 0;
        video.play().catch((error) => {
          // Only log real errors, ignore AbortError from rapid pause/remove
          if (error.name !== 'AbortError') {
            console.log('Error playing video', error);
          }
        });
      });

      // [ADA]
      tile.addEventListener('focus', (e) => {
        tile.dispatchEvent(new Event('mouseenter'));
      });
    }
  };

  // Stops video when tile leaves viewport or when the video ended
  stopsVideo = (tile) => {
    const video = tile.querySelector('video');
    if (!video) {
      return;
    }

    if (!video.paused) {
      video.pause();
    }

    video.currentTime = 0;
  };
}
